import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Only the fallback language is statically imported so it ships in the
// initial bundle and is available during SSR. The other locales are
// dynamically imported on demand (post-hydration) to reduce unused JS.
import en from "./locales/en.json";

export const SUPPORTED_LANGUAGES = ["pt", "en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  pt: "Português",
  en: "English",
  es: "Español",
};

const isBrowser = typeof window !== "undefined";

const loadedLanguages = new Set<SupportedLanguage>(["en"]);
const inflight = new Map<SupportedLanguage, Promise<void>>();

// Guards a ONE-SHOT reload when a dynamic import fails. A failed import in the
// browser almost always means the page was loaded before a deploy and the
// hashed chunk it references (e.g. a locale bundle) no longer exists on the
// server. We reload once to pull the fresh bundle; the sessionStorage flag
// prevents a genuine failure from looping forever. It's cleared on any
// successful load so a later deploy can trigger another one-shot reload.
const CHUNK_RELOAD_KEY = "i18n.chunkReload";

function reloadOnceForStaleChunk(): boolean {
  if (!isBrowser) return false;
  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
  } catch {
    // sessionStorage unavailable (private mode) — skip the guard
  }
  if (alreadyReloaded) return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}

function clearStaleChunkGuard(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // ignore (SSR / private mode)
  }
}

// Lazy route chunks can also 404 after a deploy. Vite dispatches
// `vite:preloadError` for those — reload once to recover, same as locales.
if (isBrowser) {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}

async function loadLanguage(lng: SupportedLanguage): Promise<void> {
  if (loadedLanguages.has(lng)) return;
  const existing = inflight.get(lng);
  if (existing) return existing;

  const promise = (async () => {
    let mod: { default: Record<string, unknown> };
    try {
      if (lng === "pt") {
        mod = await import("./locales/pt.json");
      } else if (lng === "es") {
        mod = await import("./locales/es.json");
      } else {
        return;
      }
    } catch (err) {
      // Stale hashed locale chunk after a deploy → reload once for the fresh
      // bundle. If we do reload, hang so callers don't surface the error (e.g.
      // a "Could not save" toast) before the page navigates away.
      if (reloadOnceForStaleChunk()) {
        await new Promise<never>(() => {});
      }
      throw err;
    }
    i18n.addResourceBundle(lng, "translation", mod.default, true, true);
    loadedLanguages.add(lng);
    clearStaleChunkGuard();
  })();

  inflight.set(lng, promise);
  try {
    await promise;
  } finally {
    inflight.delete(lng);
  }
}

if (!i18n.isInitialized) {
  const instance = i18n.use(initReactI18next);
  if (isBrowser) {
    instance.use(LanguageDetector);
  }
  instance.init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: "en",
    // On the server there is no localStorage/navigator — pin to "en" so SSR
    // is deterministic and bundled resources always resolve. On the client
    // LanguageDetector swaps to the user's preferred language post-hydration.
    lng: isBrowser ? undefined : "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Map "pt-BR" -> "pt", "en-US" -> "en", "es-MX" -> "es"
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    // If a key resolves to an object/array but is missing in the active
    // language, return an empty array instead of the key string. Combined
    // with the tArray helper this prevents `.map is not a function` crashes.
    returnedObjectHandler: (_key, value, _options) => {
      if (Array.isArray(value)) return value;
      if (value && typeof value === "object") return value;
      return [];
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "app.lang",
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

  if (isBrowser) {
    // Lazy-load the detected language bundle (if not already English) and
    // re-apply the language so components re-render with the loaded strings.
    const detected = (i18n.resolvedLanguage ?? i18n.language ?? "en") as string;
    const normalized = detected.split("-")[0] as SupportedLanguage;
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(normalized) && normalized !== "en") {
      void loadLanguage(normalized).then(() => {
        void i18n.changeLanguage(normalized);
      });
    }

    // Ensure any future language switch lazy-loads the bundle first AND
    // persists the choice to localStorage so it's restored on next visit.
    i18n.on("languageChanged", (lng: string) => {
      const next = lng.split("-")[0] as SupportedLanguage;
      if ((SUPPORTED_LANGUAGES as readonly string[]).includes(next)) {
        try {
          window.localStorage.setItem("app.lang", next);
        } catch {
          // ignore storage errors (private mode, etc.)
        }
        if (!loadedLanguages.has(next)) {
          void loadLanguage(next);
        }
      }
    });

    // Patch fetch so every request to our own server functions carries the
    // current UI language in Accept-Language. Server-side translators rely on
    // this header to localize thrown error messages.
    if (
      typeof window !== "undefined" &&
      !(window as unknown as { __i18nFetchPatched?: boolean }).__i18nFetchPatched
    ) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        try {
          const lang = (i18n.resolvedLanguage ?? i18n.language ?? "pt").split("-")[0];
          const headers = new Headers(
            init?.headers ?? (input instanceof Request ? input.headers : undefined),
          );
          if (!headers.has("Accept-Language")) {
            headers.set("Accept-Language", lang);
          }
          return originalFetch(input, { ...init, headers });
        } catch {
          return originalFetch(input, init);
        }
      };
      (window as unknown as { __i18nFetchPatched?: boolean }).__i18nFetchPatched = true;
    }
  }
}

export { loadLanguage };
export default i18n;
